// Workflow schema: the serializable model behind the Workflows screen.
//
// A workflow is a typed, JSON-serializable graph that a future builder UI can
// EMIT and a backend engine can EXECUTE. The same definition also drives the
// read-only "How it works" diagram via `toDisplaySteps`, so the picture an
// owner sees is generated from the exact object the engine runs. No drift
// between depiction and behaviour.
//
// Design. Schema-driven, reusable blocks (n8n `INodeProperties` model)
//   Every block is the SAME shape: `{ id, type, parameters }`. A block's
//   behaviour is fixed by its `type`; its `parameters` make it specific. Each
//   type declares its configurable parameters ONCE as a uniform
//   `ParameterSpec[]` in the `NODE_TYPES` registry. Exactly the field schema a
//   builder renders a config form from, and the diagram renders value chips
//   from. There are no per-kind node shapes and no per-kind display code: one
//   registry + one generic adapter cover every block.
//
//   The primitives: Trigger, Condition (with exceptions), Decision (AI),
//   Execution, Delay/Wait, and Loop ("Repeat until"). Condition and Loop are
//   the two Control blocks. "Approval" is NOT a primitive. It's the
//   `requiresApproval` parameter of an Execution, so the human gate travels with
//   the action it guards. Conditions branch by exception; everything else is
//   linear by default (array order), with optional explicit `edges`. A Loop
//   repeats the contiguous blocks below it (see loopSpan); its return to the
//   top is part of the block, never an authored backward edge.

export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };

// Display layer. What the "How it works" diagram consumes.

/**
 * Block kinds the diagram knows how to render. The first five are the canonical
 * authoring primitives; `action` is a legacy display kind kept for diagram
 * compatibility (no authored node uses it).
 */
export type BlockKind =
  | "trigger"
  | "condition"
  | "decision"
  | "execution"
  | "delay"
  | "loop"
  | "action";

/**
 * One configurable parameter on a block, rendered as a "label: value-chip" row.
 * A block does fixed logic; its params are the editable knobs. `emphasis: "ai"`
 * marks values that come from / feed an AI Decision (rendered in violet).
 */
export interface DisplayParam {
  label: string;
  values: string[];
  emphasis?: "default" | "ai";
  href?: string;
}

/** One rendered card in the diagram. Produced by `toDisplaySteps`. */
export interface DisplayStep {
  kind: BlockKind;
  label: string;
  description?: string;
  /** The block's editable parameters, shown as value chips. */
  params?: DisplayParam[];
  /** Condition blocks only: the "Skip if" exception lines. */
  exceptions?: string[];
  /** Execution blocks only: true when the action waits for owner approval. */
  requiresApproval?: boolean;
  /** Loop blocks only: the "Stop when" rule lines. */
  stopRules?: string[];
  /** Loop blocks only: how many blocks below it repeat each round. */
  loopBodyLength?: number;
  /** Set on every block inside a loop: that loop block's id. */
  loopId?: string;
}

// Authoring layer. The typed graph a builder emits.

// Triggers

/**
 * Events the platform can start a workflow on. Mirrors the backend automation
 * surface; a builder shows these as a picklist. Extend as the engine grows.
 */
export type TriggerEvent =
  | "trial_or_first_visit_completed"
  | "booking_upcoming" // params: { hoursBefore: number }
  | "no_show"
  | "booking_completed"
  | "session_finished" // params: { hoursAfter: number, firstSessionOnly?: boolean }
  | "lead_replied_then_went_quiet"
  | "no_reply_to_last_message"
  | "message_received"
  | "booking_created"
  | "booking_cancelled"
  | "form_submitted"
  | "complaint_received"
  | "case_opened"
  | "case_acknowledged"
  | "case_resolved"
  | "case_overdue";

// Conditions

/**
 * A field the engine exposes about the run's subject (customer, booking,
 * business). Treated as a catalog string the builder resolves from an
 * engine-provided schema, e.g. "customer.hasActivePackage".
 */
export type ContextField = string;

export type Operator =
  | "eq"
  | "neq"
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "in"
  | "is_true"
  | "is_false"
  | "within_days"
  | "contains"; // array-valued field includes the value (e.g. customer tags)

/**
 * A single machine-evaluable test. `label` is the human sentence shown under
 * "Skip if"; `field`/`operator`/`value` are what the engine actually checks.
 */
export interface Predicate {
  label: string;
  field: ContextField;
  operator: Operator;
  value?: JsonValue;
}

/**
 * One exception RULE: predicates AND-ed together ("no reply AND 3+ days").
 * Rules are OR-ed by the Condition. Any rule tripping takes the skip branch.
 * The Zapier filter shape: OR across groups, AND within a group.
 */
export interface PredicateGroup {
  label: string;
  all: Predicate[];
}

/** What `parameters.exceptions` may hold. Definitions published before
 *  AND-groups existed store bare Predicates. Read them as groups of one. */
export type ExceptionRule = Predicate | PredicateGroup;

/**
 * THE reader for `parameters.exceptions`. Every consumer (engine, dry run,
 * diagram, builder, publish validation) normalizes through here so old bare
 * predicates and new groups always evaluate and render identically.
 */
export function normalizeExceptions(raw: unknown): PredicateGroup[] {
  if (!Array.isArray(raw)) return [];
  const groups: PredicateGroup[] = [];
  for (const entry of raw as ExceptionRule[]) {
    if (!entry || typeof entry !== "object") continue;
    if ("all" in entry && Array.isArray((entry as PredicateGroup).all)) {
      const g = entry as PredicateGroup;
      groups.push({
        label: g.label ||
          g.all.map((p) => p.label).filter(Boolean).join(" and "),
        all: g.all,
      });
    } else if ("field" in entry) {
      const p = entry as Predicate;
      groups.push({ label: p.label, all: [p] });
    }
  }
  return groups;
}

// Decisions (AI)

/** A named, typed field the AI Decision must produce. */
export interface OutputField {
  key: string;
  type: "string" | "number" | "money" | "boolean" | "enum";
  description?: string;
  /** Allowed values when `type` is "enum". */
  enumValues?: string[];
}

/** Bounds the engine enforces on a Decision's output before it can be used. */
export interface Guardrails {
  maxAmount?: number;
  tone?: string;
  mustInclude?: string[];
  mustAvoid?: string[];
}

// Executions

/** Concrete actions a workflow can carry out. */
export type ExecutionAction =
  | "send_message"
  | "request_feedback"
  | "add_tag"
  | "remove_tag"
  | "save_to_profile"
  | "schedule_followup"
  | "fill_from_waitlist"
  | "escalate_conversation"
  | "cancel_booking"
  | "reschedule_booking"
  | "notify_owner"
  | "open_case"
  | "pause_outreach"
  | "reply_to_complaint"
  | "issue_recovery_offer";

/**
 * Channel CONSTRAINT on a send. "Only run this step when the conversation is
 * on X". NOT a delivery selector: the engine has exactly one place to send
 * (the conversation the subject already lives on), so choosing a destination
 * was never possible. The old `channel` parameter pretended otherwise and was
 * silently ignored at runtime; this replaces it under a new key (`onChannel`)
 * so legacy snapshots carrying `channel: "email"` stay inert and unread.
 * I.e. they keep behaving exactly as they do today ("any").
 *
 * Values are exactly the channels with a live connector
 * (connectors/registry.ts). Offering sms/email/web was the second half of
 * the same lie.
 */
export type ChannelConstraint =
  | "any"
  | "whatsapp"
  | "instagram"
  | "telegram"
  | "facebook_messenger"
  | "line";

/**
 * The person a customer-facing message belongs to. Stage 1 intentionally
 * exposes only the run's original subject: actions such as fill_from_waitlist
 * may cause another subsystem to contact a replacement, but they do NOT
 * silently retarget this workflow. Making that explicit prevents a later Send
 * block labelled "notify replacement" from actually messaging the original
 * customer.
 */
export type MessageRecipient = "workflow_subject";

/**
 * How an Execution input gets its value: either bound to a field on an upstream
 * Decision's output, or a static literal chosen by the author.
 */
export type Binding =
  | { source: "decision"; nodeId: string; field: string }
  | { source: "static"; value: JsonValue };

// The uniform node envelope

/** The authoring primitives. Every node has one of these as its `type`. */
export type NodeType =
  | "trigger"
  | "condition"
  | "decision"
  | "execution"
  | "delay"
  | "loop";

/**
 * A single block. Uniform across all kinds: identity + a flat `parameters` bag
 * whose meaning is given by the node's `type` via the NODE_TYPES registry. This
 * is exactly what a builder UI emits and the engine reads.
 */
export interface WorkflowNode {
  /** Stable id, referenced by edges and bindings. */
  id: string;
  type: NodeType;
  /** Author-facing title shown on the diagram card. Falls back to the type label. */
  label?: string;
  /** Author-facing one-liner shown in the card body. */
  description?: string;
  /** Values keyed by ParameterSpec.key. Structured values (Predicate[],
   *  OutputField[], Binding) live here too. */
  parameters: Record<string, JsonValue>;
}

// Block registry. Every block type's uniform parameter schema.
// (The n8n `INodeProperties` model: one field schema per node type, the UI
// renders config forms / value chips generically from it.)

/** How a parameter is rendered and validated. */
export type ParameterType =
  | "text"
  | "number"
  | "boolean"
  | "duration" // minutes → "24h" / "30 min"
  | "options" // enum select (event, channel, action, operator, mode)
  | "knowledgeRef" // links to the agent's Knowledge (Decision tone)
  | "predicateList" // condition exceptions ("Skip if" rules)
  | "outputContract" // decision output fields
  | "binding"; // bound to an upstream Decision output, or a static literal

/** One configurable field on a block. The uniform descriptor a builder renders. */
export interface ParameterSpec {
  /** Machine name; read from `node.parameters[key]`. */
  key: string;
  /** Display label (left column of a value-chip row / builder field). */
  label: string;
  type: ParameterType;
  /** For `options`: the allowed (value → label) choices. */
  options?: { value: string; label: string }[];
  default?: JsonValue;
  /** Violet AI styling on the value chip. */
  emphasis?: "default" | "ai";
  /** Suffix appended to `number` values, e.g. "h". */
  suffix?: string;
  /** Optional params may be absent from `node.parameters`. */
  optional?: boolean;
  /**
   * Structural params that define the block's identity/behaviour but are NOT
   * shown as value chips on the read-only diagram (they surface as the card
   * title, the "Skip if" list, the approval badge, or branch layout instead).
   * They are still real, builder-configurable parameters.
   */
  chip?: false;
}

/** A registered block type: its label, icon hint, and uniform parameter schema. */
export interface NodeTypeDef {
  type: NodeType;
  label: string;
  description?: string;
  /** Drives the "Powered by Dibs AI" badge + dibs-red border. */
  usesAI?: boolean;
  parameters: ParameterSpec[];
}

// Human labels for enum values, shared by the registry option lists, the
// display adapter, and the builder UI's pickers.
export const TRIGGER_EVENT_LABELS: Record<TriggerEvent, string> = {
  trial_or_first_visit_completed: "Trial or first visit completed",
  booking_upcoming: "Booking coming up",
  no_show: "Customer no-showed",
  booking_completed: "Booking completed",
  session_finished: "Session finished",
  lead_replied_then_went_quiet: "Lead went quiet",
  no_reply_to_last_message: "No reply to your message",
  message_received: "Someone messages you",
  booking_created: "Booking created",
  booking_cancelled: "Booking cancelled",
  form_submitted: "Tally form submitted",
  complaint_received: "Complaint received",
  case_opened: "Case opened",
  case_acknowledged: "Case acknowledged",
  case_resolved: "Case resolved",
  case_overdue: "Case deadline missed",
};

/** One-line pickers help: what fires the trigger, and how often it can fire.
 *  Shown under each option in the builder's trigger dropdowns. */
export const TRIGGER_EVENT_DESCRIPTIONS: Record<TriggerEvent, string> = {
  trial_or_first_visit_completed:
    "A customer finishes their very first session. Fires once per customer.",
  booking_upcoming:
    "A confirmed booking approaches. Fires once per booking, at the time you set.",
  no_show: "A booking is marked as a no-show. Fires once per booking.",
  booking_completed: "A booking is marked completed. Fires once per booking.",
  session_finished:
    "A booked session's end time passes, whether or not anyone marks attendance. Fires once per booking, the hours after it ends that you set. Skips cancelled and no-show bookings.",
  lead_replied_then_went_quiet:
    "A lead replied but the conversation stalled. Fires once per conversation.",
  no_reply_to_last_message:
    "Your message has had no reply for 2 hours, including people you messaged first who never replied. Messages your automations send don't count. Fires once per conversation.",
  message_received:
    "Runs when a customer messages you on WhatsApp, Instagram, Telegram, Messenger or LINE. Runs on every message unless you limit it below.",
  booking_created: "A new booking is made. Fires once per booking.",
  booking_cancelled: "A booking is cancelled. Fires once per booking.",
  form_submitted:
    "A mapped Tally response is safely absorbed into Dibs CRM. Fires once per submission.",
  complaint_received:
    "A new complaint arrives from a connected review or email source. Fires once per complaint.",
  case_opened:
    "A customer case or operational incident is created. Fires once per case.",
  case_acknowledged:
    "A team member accepts ownership of a case. Fires once per acknowledgement.",
  case_resolved:
    "A team member records a case outcome and resolves it. Fires once per resolution.",
  case_overdue:
    "A case misses its acknowledgement or resolution deadline. Fires once per missed deadline.",
};

/** Dropdown grouping for the trigger picker (Zapier-style categories). */
export const TRIGGER_EVENT_GROUPS: ReadonlyArray<
  { label: string; events: readonly TriggerEvent[] }
> = [
  {
    label: "Bookings",
    events: [
      "booking_created",
      "booking_upcoming",
      "booking_completed",
      "session_finished",
      "booking_cancelled",
      "no_show",
    ],
  },
  { label: "Customers", events: ["trial_or_first_visit_completed"] },
  {
    label: "Conversations",
    events: [
      "message_received",
      "lead_replied_then_went_quiet",
      "no_reply_to_last_message",
    ],
  },
  { label: "Forms & CRM", events: ["form_submitted"] },
  { label: "Feedback & complaints", events: ["complaint_received"] },
  {
    label: "Cases & incidents",
    events: [
      "case_opened",
      "case_acknowledged",
      "case_resolved",
      "case_overdue",
    ],
  },
];

/** Events that exist as real-time domain events and can therefore wake an
 * `until_event` Wait. Time-based pseudo-events (booking_upcoming / lead quiet)
 * and the derived first-visit trigger are deliberately absent. */
export const WAIT_EVENT_EVENTS: readonly TriggerEvent[] = [
  "booking_created",
  "booking_completed",
  "booking_cancelled",
  "no_show",
  "case_acknowledged",
  "case_resolved",
  "case_overdue",
];

export const WAIT_EVENT_LABELS: Partial<Record<TriggerEvent, string>> = Object
  .fromEntries(
    WAIT_EVENT_EVENTS.map((event) => [event, TRIGGER_EVENT_LABELS[event]]),
  ) as Partial<Record<TriggerEvent, string>>;

/** Triggers whose run subject is a booking. The only ones a service
 *  (`listingId`) filter can apply to. */
export const BOOKING_TRIGGER_EVENTS: readonly TriggerEvent[] = [
  "trial_or_first_visit_completed",
  "booking_upcoming",
  "no_show",
  "booking_completed",
  "session_finished",
  "booking_created",
  "booking_cancelled",
];

export const ACTION_LABELS: Record<ExecutionAction, string> = {
  send_message: "Send message",
  request_feedback: "Request private feedback",
  add_tag: "Add tag",
  remove_tag: "Remove tag",
  save_to_profile: "Save to customer profile",
  schedule_followup: "Schedule follow-up",
  fill_from_waitlist: "Fill from waitlist",
  escalate_conversation: "Hand over to a human",
  cancel_booking: "Cancel the booking",
  reschedule_booking: "Move the booking",
  notify_owner: "Notify me",
  open_case: "Open a case",
  pause_outreach: "Pause customer outreach",
  reply_to_complaint: "Reply to complaint",
  issue_recovery_offer: "Issue recovery offer",
};

/** One-line picker help: what the action does, and what it needs to work.
 *  Same treatment as TRIGGER_EVENT_DESCRIPTIONS. */
export const ACTION_DESCRIPTIONS: Record<ExecutionAction, string> = {
  send_message:
    "Messages the customer on the channel they already chat with you on.",
  request_feedback:
    "Sends a one-use private feedback link; poor or sensitive responses become human-owned cases.",
  add_tag:
    "Stamps a label on the customer's record — useful for segmenting later.",
  remove_tag:
    "Clears a label from the customer's record. Nothing else can clear tags.",
  save_to_profile:
    "Adds a note to the customer's profile, such as an AI step's summary of what they told you. Goes to your team, never to the customer.",
  schedule_followup:
    "Queues a message to go out later. The workflow doesn't wait — use a Wait block for that.",
  fill_from_waitlist:
    "Asks you to approve releasing the slot so waitlist auto-fill can offer it on.",
  escalate_conversation:
    "Stops the AI replying and hands the conversation to you by email.",
  cancel_booking:
    "Cancels the booking and offers the slot to your waitlist. Always asks you first.",
  reschedule_booking:
    "Pushes the booking back by a set number of days, keeping the same time. Always asks you first.",
  notify_owner:
    "Emails you about this run. Goes to you, never to the customer.",
  open_case:
    "Creates an owned case with urgency, a responsible role and response deadlines.",
  pause_outreach:
    "Stops proactive sales and follow-up messages for this customer for a set time.",
  reply_to_complaint:
    "Publishes the reviewed response on Google or emails it to the complainant. Always asks you first.",
  issue_recovery_offer:
    "Creates and emails one capped, one-use recovery code under your policy. Always asks you first.",
};

/**
 * Actions that ALWAYS queue for owner approval, whatever the node says. These
 * undo real-world commitments a customer is relying on, so "the workflow did
 * it while I slept" is not an acceptable outcome. The engine forces it at
 * dispatch (a rail outside the authorable graph, invariant 3), the builder
 * renders the toggle locked ON, and the diagram reports it as approval-gated
 * even when the node's own flag is unset.
 *
 * Lives here rather than in capabilities.ts because the display layer needs it
 * and capabilities.ts already imports THIS module.
 */
export const ALWAYS_APPROVE_ACTIONS: readonly ExecutionAction[] = [
  "fill_from_waitlist",
  "cancel_booking",
  "reschedule_booking",
  "reply_to_complaint",
  "issue_recovery_offer",
];

/** True when this node will wait for approval. Its own flag OR a forced one. */
export function nodeRequiresApproval(node: WorkflowNode): boolean {
  if (node.type !== "execution") return false;
  if (node.parameters.requiresApproval === true) return true;
  return ALWAYS_APPROVE_ACTIONS.includes(
    node.parameters.action as ExecutionAction,
  );
}

/** Dropdown grouping for the action picker (Zapier-style categories). */
export const ACTION_GROUPS: ReadonlyArray<
  { label: string; actions: readonly ExecutionAction[] }
> = [
  {
    label: "Message",
    actions: [
      "send_message",
      "request_feedback",
      "schedule_followup",
      "reply_to_complaint",
    ],
  },
  { label: "Recovery", actions: ["issue_recovery_offer"] },
  {
    label: "Customer record",
    actions: ["add_tag", "remove_tag", "save_to_profile"],
  },
  {
    label: "Booking",
    actions: ["fill_from_waitlist", "reschedule_booking", "cancel_booking"],
  },
  { label: "Cases & safety", actions: ["open_case", "pause_outreach"] },
  { label: "You", actions: ["notify_owner", "escalate_conversation"] },
];

export const CHANNEL_LABELS: Record<ChannelConstraint, string> = {
  any: "Any channel",
  whatsapp: "WhatsApp",
  instagram: "Instagram",
  telegram: "Telegram",
  facebook_messenger: "Facebook Messenger",
  line: "LINE",
};

export const MESSAGE_RECIPIENT_LABELS: Record<MessageRecipient, string> = {
  workflow_subject: "Customer this workflow started for",
};

const toOptions = (labels: Record<string, string>) =>
  Object.entries(labels).map(([value, label]) => ({ value, label }));

/**
 * The block registry. Five reusable primitives, each parameterized. A builder
 * renders a config form from `parameters`; the diagram renders value chips from
 * the same array. Add a block type here and both surfaces pick it up.
 */
export const NODE_TYPES: Record<NodeType, NodeTypeDef> = {
  trigger: {
    type: "trigger",
    label: "Trigger",
    description: "The event that starts the workflow.",
    parameters: [
      {
        key: "event",
        label: "When",
        type: "options",
        options: toOptions(TRIGGER_EVENT_LABELS),
        chip: false,
      },
      {
        key: "hoursBefore",
        label: "Duration before",
        type: "number",
        suffix: "h",
        optional: true,
      },
      // session_finished only: how long after the session's end time it
      // fires, and whether only the customer's first session here counts.
      {
        key: "hoursAfter",
        label: "Hours after it ends",
        type: "number",
        suffix: "h",
        optional: true,
      },
      {
        key: "firstSessionOnly",
        label: "First session only",
        type: "boolean",
        optional: true,
      },
      // Trigger-level filters. Scope WHICH occurrences fire, Zapier-style.
      // listingId is the machine value the engine matches on; listingName is
      // the denormalized display label (diagram chips are pure and can't
      // resolve ids). The builder always writes both together.
      {
        key: "listingId",
        label: "Service",
        type: "text",
        optional: true,
        chip: false,
      },
      { key: "listingName", label: "Only for", type: "text", optional: true },
      {
        key: "formId",
        label: "Tally form ID",
        type: "text",
        optional: true,
        chip: false,
      },
      { key: "formName", label: "Only for form", type: "text", optional: true },
      // booking_cancelled only: fire only when the cancellation lands within
      // this many hours of the session start (late-cancel scoping).
      {
        key: "cancelledWithinHours",
        label: "Cancelled within",
        type: "number",
        suffix: "h",
        optional: true,
      },
      // message_received only. The subject of a message trigger is a
      // CONVERSATION, not a booking, so neither listingId nor the booking
      // filters above apply to it \u2014 these two are its whole scoping surface.
      //
      // messageChannel reuses ChannelConstraint ("any" + the live connectors)
      // rather than a parallel list, so the trigger can never offer a channel
      // no inbound webhook actually delivers on.
      {
        key: "messageChannel",
        label: "On channel",
        type: "options",
        options: toOptions(CHANNEL_LABELS),
        default: "any",
        optional: true,
      },
      // The lead-qualification scope. NOT "first message in this thread" \u2014
      // that would re-fire for a regular who happens to start a new chat. This
      // means the sender (the number / account) has never messaged the business
      // before, on any thread, which is what "a new enquiry" actually means.
      {
        key: "newSenderOnly",
        label: "New enquiries only",
        type: "boolean",
        optional: true,
      },
    ],
  },
  condition: {
    type: "condition",
    label: "Condition",
    description:
      "A simple check that must pass to continue, with any 'skip if' exceptions.",
    parameters: [
      {
        key: "exceptions",
        label: "Skip if",
        type: "predicateList",
        chip: false,
      },
    ],
  },
  decision: {
    type: "decision",
    label: "Decision",
    description: "Where the AI uses judgment to tailor the outcome.",
    usesAI: true,
    parameters: [
      { key: "goal", label: "Goal", type: "text", chip: false },
      {
        key: "output",
        label: "Produces",
        type: "outputContract",
        emphasis: "ai",
      },
      // Free text the owner writes (was typed knowledgeRef, which rendered a
      // misleading "Knowledge" chip for a value like "Friendly, light").
      { key: "tone", label: "Tone", type: "text", optional: true },
      // Both hold string[] (the builder splits comma-separated input) and are
      // enforced DETERMINISTICALLY after generation, not just prompted.
      {
        key: "mustAvoid",
        label: "Must avoid",
        type: "text",
        optional: true,
        chip: false,
      },
      {
        key: "mustInclude",
        label: "Must include",
        type: "text",
        optional: true,
        chip: false,
      },
    ],
  },
  execution: {
    type: "execution",
    label: "Execution",
    description: "The action the workflow carries out.",
    parameters: [
      {
        key: "action",
        label: "Action",
        type: "options",
        options: toOptions(ACTION_LABELS),
        chip: false,
      },
      {
        key: "recipient",
        label: "Recipient",
        type: "options",
        options: toOptions(MESSAGE_RECIPIENT_LABELS),
        optional: true,
      },
      // A CONSTRAINT, not a destination. See ChannelConstraint. Deliberately
      // keyed `onChannel`, not `channel`: the old key stays unread on legacy
      // snapshots so their behaviour is unchanged.
      {
        key: "onChannel",
        label: "Only on",
        type: "options",
        options: toOptions(CHANNEL_LABELS),
        optional: true,
      },
      {
        key: "message",
        label: "Message",
        type: "binding",
        emphasis: "ai",
        optional: true,
      },
      // add_tag / remove_tag only: the tag text to stamp on (or clear from)
      // the contact. A dedicated param. It must never share the `message`
      // slot, whose AI binding semantics don't apply to tags.
      { key: "tag", label: "Tag", type: "text", optional: true },
      // save_to_profile only: the note's title on the customer's profile.
      // The note's body is the `message` binding (AI-written or fixed text).
      { key: "noteTitle", label: "Note title", type: "text", optional: true },
      // escalate_conversation / cancel_booking / notify_owner: the owner-facing
      // "why". Never shown to the customer. It lands in the handoff email, the
      // approval card, or the notification.
      { key: "reason", label: "Reason", type: "text", optional: true },
      {
        key: "caseCategory",
        label: "Category",
        type: "options",
        options: [
          { value: "clinical", label: "Clinical concern" },
          { value: "safety", label: "Safety concern" },
          { value: "service", label: "Service complaint" },
          { value: "billing", label: "Billing issue" },
          { value: "privacy", label: "Privacy concern" },
          { value: "booking", label: "Booking issue" },
          { value: "operational", label: "Operational incident" },
          { value: "other", label: "Other" },
        ],
        optional: true,
      },
      {
        key: "caseSeverity",
        label: "Urgency",
        type: "options",
        options: [
          { value: "emergency", label: "Emergency" },
          { value: "high", label: "High" },
          { value: "normal", label: "Normal" },
          { value: "low", label: "Low" },
        ],
        optional: true,
      },
      {
        key: "assignedRole",
        label: "Responsible role",
        type: "options",
        options: [
          { value: "owner", label: "Owner" },
          { value: "clinic_manager", label: "Clinic manager" },
          { value: "clinician", label: "Clinician" },
          { value: "front_desk", label: "Front desk" },
          { value: "billing", label: "Billing" },
          { value: "operations", label: "Operations" },
          { value: "outlet_manager", label: "Outlet manager" },
          { value: "security", label: "Security" },
        ],
        optional: true,
      },
      {
        key: "acknowledgeWithinMinutes",
        label: "Acknowledge within",
        type: "duration",
        optional: true,
      },
      {
        key: "resolveWithinMinutes",
        label: "Resolve within",
        type: "duration",
        optional: true,
      },
      {
        key: "pauseOutreach",
        label: "Pause outreach",
        type: "boolean",
        optional: true,
      },
      {
        key: "holdDays",
        label: "Pause for",
        type: "number",
        suffix: "d",
        optional: true,
      },
      // send_message only: reach booking customers who have no conversation
      // (booked on the web, never chatted) by email. Opt-IN. Default off
      // keeps every already-published workflow behaving as it does today.
      {
        key: "emailFallback",
        label: "Email fallback",
        type: "boolean",
        optional: true,
        chip: false,
      },
      // send_message only: the Meta-approved WhatsApp template (a
      // business_message_templates row, purpose 'workflow') sent instead of
      // the message once the customer's 24-hour window has closed. Inside the
      // window the message itself goes out. The id is what the engine reads;
      // the name is a display copy for the diagram chip.
      // whatsappTemplateAuto: Dibs makes the template from this step's fixed
      // text and submits it to Meta itself. Publishing links the template it
      // created into whatsappTemplateId / whatsappTemplateName.
      {
        key: "whatsappTemplateAuto",
        label: "Automatic WhatsApp template",
        type: "boolean",
        optional: true,
        chip: false,
      },
      {
        key: "whatsappTemplateId",
        label: "WhatsApp template",
        type: "text",
        optional: true,
        chip: false,
      },
      {
        key: "whatsappTemplateName",
        label: "After 24h, send template",
        type: "text",
        optional: true,
      },
      {
        key: "feedbackExpiresDays",
        label: "Link expires after",
        type: "number",
        suffix: "d",
        optional: true,
      },
      // reschedule_booking only: how far to push the booking back. A RELATIVE
      // offset is the only thing an author can express. An absolute date
      // isn't knowable when the workflow is written, and there is no
      // structured-date binding for a Decision to supply one.
      {
        key: "rescheduleByDays",
        label: "Move back by",
        type: "number",
        suffix: "d",
        optional: true,
      },
      // schedule_followup only: how long after this step the follow-up lands
      // (a scheduled_messages row; the sweep delivers it with send-time
      // window gating). The run does NOT wait. Use a Wait block for that.
      {
        key: "followupDelayMinutes",
        label: "Send after",
        type: "duration",
        optional: true,
      },
      {
        key: "requiresApproval",
        label: "Approval",
        type: "boolean",
        optional: true,
        chip: false,
      },
    ],
  },
  delay: {
    type: "delay",
    label: "Wait",
    description: "A time gap — waits a fixed duration or until an event fires.",
    parameters: [
      {
        key: "mode",
        label: "Mode",
        type: "options",
        options: [{ value: "duration", label: "Duration" }, {
          value: "until_event",
          label: "Until event",
        }, {
          // Wakes on the customer's next message in this conversation, or
          // when timeoutMinutes runs out (a Condition on
          // conversation.hasReplied tells the two apart).
          value: "until_reply",
          label: "Until they reply",
        }],
        chip: false,
      },
      {
        key: "durationMinutes",
        label: "Wait",
        type: "duration",
        optional: true,
      },
      {
        key: "untilEvent",
        label: "Wait until",
        type: "options",
        options: toOptions(WAIT_EVENT_LABELS as Record<string, string>),
        optional: true,
      },
      {
        key: "timeoutMinutes",
        label: "Timeout",
        type: "duration",
        optional: true,
        chip: false,
      },
      // Agentic timing (bounded): the AI may EXTEND this wait. Never shorten
      // it below the authored duration, when the conversation warrants it
      // (e.g. the customer mentioned a bereavement). See timingAdvisor.
      {
        key: "aiAdjustable",
        label: "AI timing",
        type: "boolean",
        optional: true,
        emphasis: "ai",
      },
      {
        key: "maxExtensionMinutes",
        label: "Max extra wait",
        type: "duration",
        optional: true,
      },
    ],
  },
  // The second Control block. A while loop: before every round it checks the
  // stop rules against FRESH context (the same predicates a Condition uses);
  // while none match and rounds remain, it runs the blocks from the one below
  // it through `bodyEnd`, then comes back here. When a rule matches or the
  // rounds run out, the run carries on after `bodyEnd`.
  loop: {
    type: "loop",
    label: "Repeat until",
    description:
      "Repeats the blocks inside it until a stop rule matches or the rounds run out.",
    parameters: [
      {
        key: "stopWhen",
        label: "Stop when",
        type: "predicateList",
        chip: false,
      },
      { key: "maxIterations", label: "Max rounds", type: "number" },
      // Id of the last block inside the loop. The body is every block from
      // the one directly below the loop through this one (see loopSpan).
      {
        key: "bodyEnd",
        label: "Last block inside",
        type: "text",
        optional: true,
        chip: false,
      },
    ],
  },
};

// Typed builder helpers
// Authoring ergonomics + type-safety: each returns a uniform WorkflowNode with
// the right `type` + `parameters`. This is the bridge between readable
// definitions and the envelope a builder/engine consume. `undefined` params are
// stripped so the stored object stays clean.

function node(
  type: NodeType,
  id: string,
  label: string | undefined,
  description: string | undefined,
  parameters: Record<string, JsonValue | undefined>,
): WorkflowNode {
  const clean: Record<string, JsonValue> = {};
  for (const [k, v] of Object.entries(parameters)) {
    if (v !== undefined) clean[k] = v as JsonValue;
  }
  return {
    id,
    type,
    ...(label ? { label } : {}),
    ...(description ? { description } : {}),
    parameters: clean,
  };
}

export function trigger(cfg: {
  id: string;
  event: TriggerEvent;
  hoursBefore?: number;
  hoursAfter?: number;
  firstSessionOnly?: boolean;
  listingId?: string;
  listingName?: string;
  formId?: string;
  formName?: string;
  cancelledWithinHours?: number;
  messageChannel?: ChannelConstraint;
  newSenderOnly?: boolean;
  label: string;
  description?: string;
}): WorkflowNode {
  return node("trigger", cfg.id, cfg.label, cfg.description, {
    event: cfg.event,
    hoursBefore: cfg.hoursBefore,
    hoursAfter: cfg.hoursAfter,
    firstSessionOnly: cfg.firstSessionOnly,
    listingId: cfg.listingId,
    listingName: cfg.listingName,
    formId: cfg.formId,
    formName: cfg.formName,
    cancelledWithinHours: cfg.cancelledWithinHours,
    messageChannel: cfg.messageChannel,
    newSenderOnly: cfg.newSenderOnly,
  });
}

export function condition(cfg: {
  id: string;
  exceptions: ExceptionRule[];
  label?: string;
  description?: string;
}): WorkflowNode {
  return node("condition", cfg.id, cfg.label, cfg.description, {
    exceptions: cfg.exceptions as unknown as JsonValue,
  });
}

export function decision(cfg: {
  id: string;
  goal: string;
  output: OutputField[];
  tone?: string;
  mustAvoid?: string[];
  mustInclude?: string[];
  label: string;
  description?: string;
}): WorkflowNode {
  return node("decision", cfg.id, cfg.label, cfg.description, {
    goal: cfg.goal,
    output: cfg.output as unknown as JsonValue,
    tone: cfg.tone,
    mustAvoid: cfg.mustAvoid as unknown as JsonValue | undefined,
    mustInclude: cfg.mustInclude as unknown as JsonValue | undefined,
  });
}

export function execution(cfg: {
  id: string;
  action: ExecutionAction;
  /** Constraint, not destination. See ChannelConstraint. */
  onChannel?: ChannelConstraint;
  /** Customer-facing actions always address the original run subject. */
  recipient?: MessageRecipient;
  message?: Binding;
  tag?: string;
  /** Owner-facing "why" for escalate / cancel / notify. Never customer-visible. */
  reason?: string;
  /** send_message: reach conversation-less booking customers by email. */
  emailFallback?: boolean;
  /** send_message: approved WhatsApp template sent once the 24h window has
   *  closed (business_message_templates id), plus its name for display. */
  whatsappTemplateId?: string;
  whatsappTemplateName?: string;
  /** send_message: Dibs makes and submits the template from the fixed text. */
  whatsappTemplateAuto?: boolean;
  /** request_feedback: one-use link lifetime (1-30 days). */
  feedbackExpiresDays?: number;
  /** reschedule_booking: how many days to push the booking back. */
  rescheduleByDays?: number;
  followupDelayMinutes?: number;
  caseCategory?: string;
  caseSeverity?: string;
  assignedRole?: string;
  acknowledgeWithinMinutes?: number;
  resolveWithinMinutes?: number;
  pauseOutreach?: boolean;
  holdDays?: number;
  /** save_to_profile: the note's title on the customer's profile. */
  noteTitle?: string;
  requiresApproval?: boolean;
  label: string;
  description?: string;
}): WorkflowNode {
  const recipient = cfg.recipient ??
    (cfg.action === "send_message" || cfg.action === "request_feedback" ||
        cfg.action === "schedule_followup"
      ? "workflow_subject"
      : undefined);
  return node("execution", cfg.id, cfg.label, cfg.description, {
    action: cfg.action,
    recipient,
    onChannel: cfg.onChannel,
    message: cfg.message as unknown as JsonValue | undefined,
    tag: cfg.tag,
    reason: cfg.reason,
    emailFallback: cfg.emailFallback,
    whatsappTemplateId: cfg.whatsappTemplateId,
    whatsappTemplateName: cfg.whatsappTemplateName,
    whatsappTemplateAuto: cfg.whatsappTemplateAuto,
    feedbackExpiresDays: cfg.feedbackExpiresDays,
    rescheduleByDays: cfg.rescheduleByDays,
    followupDelayMinutes: cfg.followupDelayMinutes,
    caseCategory: cfg.caseCategory,
    caseSeverity: cfg.caseSeverity,
    assignedRole: cfg.assignedRole,
    acknowledgeWithinMinutes: cfg.acknowledgeWithinMinutes,
    resolveWithinMinutes: cfg.resolveWithinMinutes,
    pauseOutreach: cfg.pauseOutreach,
    holdDays: cfg.holdDays,
    noteTitle: cfg.noteTitle,
    requiresApproval: cfg.requiresApproval,
  });
}

export function delay(cfg: {
  id: string;
  mode: "duration" | "until_event" | "until_reply";
  durationMinutes?: number;
  untilEvent?: TriggerEvent;
  timeoutMinutes?: number;
  aiAdjustable?: boolean;
  maxExtensionMinutes?: number;
  label: string;
  description?: string;
}): WorkflowNode {
  return node("delay", cfg.id, cfg.label, cfg.description, {
    mode: cfg.mode,
    durationMinutes: cfg.durationMinutes,
    untilEvent: cfg.untilEvent,
    timeoutMinutes: cfg.timeoutMinutes,
    aiAdjustable: cfg.aiAdjustable,
    maxExtensionMinutes: cfg.maxExtensionMinutes,
  });
}

export function loop(cfg: {
  id: string;
  /** Rules that end the loop, OR-ed; checked before every round. */
  stopWhen: ExceptionRule[];
  maxIterations: number;
  /** Id of the last block inside the loop. */
  bodyEnd: string;
  label?: string;
  description?: string;
}): WorkflowNode {
  return node("loop", cfg.id, cfg.label ?? "Repeat until", cfg.description, {
    stopWhen: cfg.stopWhen as unknown as JsonValue,
    maxIterations: cfg.maxIterations,
    bodyEnd: cfg.bodyEnd,
  });
}

// Loops

/** Where a loop's repeated blocks sit in `def.nodes` (indexes inclusive). */
export interface LoopSpan {
  loopId: string;
  loopIndex: number;
  /** First block inside the loop: always the one directly below it. */
  startIndex: number;
  /** Last block inside the loop (its `bodyEnd`). */
  endIndex: number;
}

/**
 * THE rule for which blocks a loop repeats: the contiguous run from the block
 * below it through its `bodyEnd`. Null when the loop has no valid body yet
 * (unset, or pointing at a block that isn't below it). The engine, the dry
 * run, the diagram, the builder and publish validation all read loops through
 * here, the same way they all route through defaultNext.
 */
export function loopSpan(
  def: WorkflowDefinition,
  loopNode: WorkflowNode,
): LoopSpan | null {
  if (loopNode.type !== "loop") return null;
  const loopIndex = def.nodes.findIndex((n) => n.id === loopNode.id);
  const bodyEnd = loopNode.parameters.bodyEnd;
  if (loopIndex < 0 || typeof bodyEnd !== "string") return null;
  const endIndex = def.nodes.findIndex((n) => n.id === bodyEnd);
  if (endIndex <= loopIndex) return null;
  return { loopId: loopNode.id, loopIndex, startIndex: loopIndex + 1, endIndex };
}

/** Every loop with a valid body, top to bottom. */
export function loopSpans(def: WorkflowDefinition): LoopSpan[] {
  return def.nodes.flatMap((n) => {
    const span = loopSpan(def, n);
    return span ? [span] : [];
  });
}

/** The loop whose body holds the block at `index`, if any. Loops never nest
 *  (validateDefinition), so there is at most one. */
export function enclosingLoop(
  def: WorkflowDefinition,
  index: number,
): LoopSpan | null {
  return loopSpans(def).find((s) =>
    index >= s.startIndex && index <= s.endIndex
  ) ?? null;
}

// Edges / routing

/** Which outcome of a node an edge represents. */
export type BranchKey = "pass" | "skip";

/**
 * Explicit routing. Only needed to override the default linear flow. E.g. to
 * send a Condition's "skip" outcome somewhere other than the end, or to jump
 * back to an earlier node. `to: "end"` terminates the run.
 */
export interface WorkflowEdge {
  from: string;
  to: string | "end";
  on?: BranchKey;
}

// Workflow

export interface WorkflowDefinition {
  id: string;
  /** Backend automation key (enable/disable + run log). Matches AUTOMATION_KEYS. */
  key: string;
  name: string;
  description: string;
  /** Bumped on every saved edit so the engine can pin in-flight runs. */
  version: number;
  /** Nodes in default execution order. The first node must be a trigger. */
  nodes: WorkflowNode[];
  /** Optional explicit routing for branches/jumps. Omit for linear flows. */
  edges?: WorkflowEdge[];
  /** Knowledge for this workflow only. Omit when it just uses the business's. */
  knowledge?: WorkflowKnowledge;
}

/**
 * The builder's "Knowledge for this workflow". Business Knowledge stays the
 * baseline for every AI reply; this layer applies on top of it while the AI
 * acts for this workflow (its Decision steps, and live chat replies to a
 * customer answering one of its messages). Where the two disagree, this wins.
 * Core safety rules always win over both. See _shared/knowledge/precedence.ts.
 */
export interface WorkflowKnowledge {
  /** Free-text instructions the owner wrote for this workflow. */
  instructions?: string;
  /** knowledge_library ids, in the owner's order. Only owner-written notes,
   *  FAQs and Markdown are used. Websites and uploads never instruct. */
  sourceIds?: string[];
}

export const MAX_WORKFLOW_KNOWLEDGE_SOURCES = 10;
export const MAX_WORKFLOW_INSTRUCTIONS_CHARS = 2000;

/** True when the workflow carries any Knowledge of its own. */
export function hasWorkflowKnowledge(def: Pick<WorkflowDefinition, "knowledge">): boolean {
  return !!def.knowledge &&
    (!!def.knowledge.instructions?.trim() ||
      (def.knowledge.sourceIds?.length ?? 0) > 0);
}

/**
 * Upgrade definitions authored before recipients were explicit. This is a
 * compatibility normalizer, not routing logic: the runtime has always sent to
 * the workflow's original subject. New definitions persist that truth so the
 * builder, validator and run log cannot imply a recipient switch.
 */
export function withExplicitMessageRecipients(
  def: WorkflowDefinition,
): WorkflowDefinition {
  let changed = false;
  const nodes = def.nodes.map((node) => {
    if (node.type !== "execution") return node;
    const action = node.parameters.action;
    if (
      (action !== "send_message" && action !== "request_feedback" &&
        action !== "schedule_followup") ||
      node.parameters.recipient != null
    ) return node;
    changed = true;
    return {
      ...node,
      parameters: { ...node.parameters, recipient: "workflow_subject" },
    };
  });
  return changed ? { ...def, nodes } : def;
}

// Runtime contract. How the engine consumes a definition. Types only; the
// implementation lives backend-side. Documented here so the model is complete.

/** Data the engine resolves ContextFields against for one run. */
export type WorkflowContext = Record<ContextField, JsonValue>;

/** Result of evaluating one node. */
export interface NodeOutcome {
  branch: BranchKey;
  output?: Record<string, JsonValue>;
}

/**
 * The engine a backend interpreter implements to run arbitrary definitions.
 * A builder UI produces `WorkflowDefinition`s; this consumes them. Not
 * implemented in the frontend. This interface documents the contract.
 */
export interface WorkflowEngine {
  evaluateCondition(node: WorkflowNode, ctx: WorkflowContext): BranchKey;
  runDecision(
    node: WorkflowNode,
    ctx: WorkflowContext,
  ): Promise<Record<string, JsonValue>>;
  runExecution(
    node: WorkflowNode,
    ctx: WorkflowContext,
    upstream: Record<string, NodeOutcome>,
  ): Promise<void>;
}

// Adapter. Definition → display steps for the "How it works" diagram.
// Driven generically by NODE_TYPES: every chip comes from a ParameterSpec, so
// there is no per-kind display code.

/** "package_offer" → "Package offer". */
function humanize(key: string): string {
  const s = key.replace(/[_-]+/g, " ").trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatDuration(mins: number): string {
  return mins >= 60 ? `${mins / 60}h` : `${mins} min`;
}

/** Format one parameter value into a display chip, or null if it has no chip. */
function formatParam(
  spec: ParameterSpec,
  value: JsonValue,
  byId: Map<string, WorkflowNode>,
): DisplayParam | null {
  switch (spec.type) {
    case "options": {
      // "any" is the do-nothing default for a constraint param. Rendering
      // "Only on: Any channel" is pure noise on the diagram.
      if (value === "any") return null;
      const opt = spec.options?.find((o) => o.value === value);
      return { label: spec.label, values: [opt?.label ?? String(value)] };
    }
    case "duration":
      return { label: spec.label, values: [formatDuration(Number(value))] };
    case "number":
      return { label: spec.label, values: [`${value}${spec.suffix ?? ""}`] };
    case "knowledgeRef":
      return {
        label: spec.label,
        values: ["Knowledge"],
        href: "?tab=agent-settings",
      };
    case "outputContract": {
      const fields = value as unknown as OutputField[];
      return {
        label: spec.label,
        values: fields.map((o) => humanize(o.key)),
        emphasis: spec.emphasis,
      };
    }
    case "binding": {
      const b = value as unknown as Binding;
      if (b.source === "decision") {
        const from = byId.get(b.nodeId);
        return {
          label: spec.label,
          values: [`${from?.label ?? "Decision"} · ${humanize(b.field)}`],
          emphasis: "ai",
        };
      }
      return { label: spec.label, values: [String(b.value)] };
    }
    case "boolean":
      return {
        label: spec.label,
        values: [value ? "Enabled" : "Off"],
        emphasis: spec.emphasis,
      };
    case "text":
    case "predicateList":
    default:
      return { label: spec.label, values: [String(value)] };
  }
}

/**
 * Execution parameters each action actually READS. Switching a node's action
 * leaves the previous action's parameters behind in `parameters`. Harmless to
 * the engine, which only reads what its case needs, but the diagram would
 * happily render a "Message" chip on an Add tag step, i.e. show the owner
 * content that will never be sent. Anything not listed here is not shown for
 * that action.
 */
const ACTION_CHIP_PARAMS: Record<ExecutionAction, readonly string[]> = {
  send_message: ["recipient", "onChannel", "message", "whatsappTemplateName"],
  request_feedback: [
    "recipient",
    "onChannel",
    "message",
    "feedbackExpiresDays",
  ],
  schedule_followup: [
    "recipient",
    "onChannel",
    "message",
    "followupDelayMinutes",
  ],
  add_tag: ["tag"],
  remove_tag: ["tag"],
  save_to_profile: ["noteTitle", "message"],
  fill_from_waitlist: [],
  escalate_conversation: ["reason"],
  cancel_booking: ["reason"],
  reschedule_booking: ["rescheduleByDays", "reason"],
  // `message` is optional and owner-facing: when bound to a Decision the AI
  // text becomes the notification body, otherwise `reason` is used. Listed
  // first so the diagram shows the AI chip above the fallback.
  notify_owner: ["message", "reason"],
  open_case: [
    "caseCategory",
    "caseSeverity",
    "assignedRole",
    "acknowledgeWithinMinutes",
    "resolveWithinMinutes",
    "pauseOutreach",
    "reason",
  ],
  pause_outreach: ["holdDays", "reason"],
  reply_to_complaint: ["message"],
  issue_recovery_offer: [],
};

/**
 * Minutes in the largest unit that still reads as a duration. "200000 minutes"
 * is not a length anyone can picture. "About 139 days" is.
 *
 * Lives here, in the display layer, because three callers need the SAME phrase
 * and any drift between them shows up as a plan disagreeing with itself: the
 * clamp gap that reports a shortened wait, the Wait block's own label, and the
 * value chip beside it.
 */
export function minutesPhrase(minutes: number): string {
  const unit = (n: number, name: string) => {
    const whole = Number.isInteger(n);
    const shown = whole ? n : Math.round(n);
    return `${whole ? "" : "about "}${shown} ${name}${shown === 1 ? "" : "s"}`;
  };
  if (minutes >= 24 * 60) return unit(minutes / (24 * 60), "day");
  if (minutes >= 60) return unit(minutes / 60, "hour");
  return unit(minutes, "minute");
}

/** The editable parameters a block exposes as value chips, drawn from NODE_TYPES. */
function nodeToParams(
  node: WorkflowNode,
  byId: Map<string, WorkflowNode>,
): DisplayParam[] {
  const def = NODE_TYPES[node.type];
  if (!def) return [];
  // For executions, narrow to the parameters the chosen action reads.
  const relevant = node.type === "execution"
    ? ACTION_CHIP_PARAMS[node.parameters.action as ExecutionAction]
    : undefined;
  const params: DisplayParam[] = [];
  for (const spec of def.parameters) {
    if (spec.chip === false) continue; // structural — surfaces elsewhere, not a chip
    if (relevant && !relevant.includes(spec.key)) continue;
    const value = node.parameters[spec.key];
    if (value === undefined || value === null) continue; // optional, unset
    const dp = formatParam(spec, value, byId);
    if (dp) params.push(dp);
  }
  return params;
}

function nodeToDisplay(
  node: WorkflowNode,
  byId: Map<string, WorkflowNode>,
): DisplayStep {
  const base: DisplayStep = {
    kind: node.type,
    label: node.type === "condition"
      ? "If-else condition"
      : (node.label ?? NODE_TYPES[node.type]?.label ?? node.type),
    description: node.description,
    params: nodeToParams(node, byId),
  };
  if (node.type === "condition") {
    base.exceptions = normalizeExceptions(node.parameters.exceptions).map((g) =>
      g.label
    );
  }
  if (node.type === "execution") {
    base.requiresApproval = nodeRequiresApproval(node);
  }
  if (node.type === "loop") {
    base.stopRules = normalizeExceptions(node.parameters.stopWhen).map((g) =>
      g.label
    );
  }
  return base;
}

/**
 * The id of the node reached by following a node's default ("pass") path.
 * THE routing rule. The interpreter (nextNodeIndex), the dry-run simulator,
 * and the diagram flattener all derive from this one function so the engine,
 * the "Test" timeline, and the picture can never disagree about the path.
 *
 * A loop's return to its top is not an edge and not part of this rule: every
 * walker that follows defaultNext passes a loop's blocks once, in order, and
 * only the engine repeats them (see loopSpan).
 */
export function defaultNext(
  def: WorkflowDefinition,
  node: WorkflowNode,
): string | undefined {
  const explicit = def.edges?.find((e) =>
    e.from === node.id && (e.on ?? "pass") === "pass"
  );
  if (explicit) return explicit.to === "end" ? undefined : explicit.to;
  const idx = def.nodes.findIndex((n) => n.id === node.id);
  return def.nodes[idx + 1]?.id;
}

/**
 * Flatten a definition into the happy-path sequence of diagram cards. Follows
 * the default ("pass") path from the first node; the "skip" branches surface as
 * each Condition's exception list rather than as separate cards. Guards against
 * cycles so a malformed graph can't loop forever.
 */
export function toDisplaySteps(def: WorkflowDefinition): DisplayStep[] {
  const byId = new Map(def.nodes.map((n) => [n.id, n] as const));
  const indexOf = new Map(def.nodes.map((n, i) => [n.id, i] as const));
  const spans = loopSpans(def);
  const steps: DisplayStep[] = [];
  const visited = new Set<string>();
  let currentId: string | undefined = def.nodes[0]?.id;
  while (currentId && !visited.has(currentId)) {
    visited.add(currentId);
    const node = byId.get(currentId);
    if (!node) break;
    const step = nodeToDisplay(node, byId);
    // A condition with a skip-edge doesn't end the run. Say where it goes,
    // as an extra line under the exceptions, so the picture stays honest.
    if (node.type === "condition") {
      const skipEdge = def.edges?.find((e) =>
        e.from === node.id && e.on === "skip"
      );
      if (skipEdge && skipEdge.to !== "end") {
        const target = byId.get(skipEdge.to);
        step.exceptions = [
          ...(step.exceptions ?? []),
          `→ if any apply, skips ahead to "${target?.label ?? skipEdge.to}"`,
        ];
      }
    }
    if (node.type === "loop") {
      const span = spans.find((sp) => sp.loopId === node.id);
      step.loopBodyLength = span ? span.endIndex - span.startIndex + 1 : 0;
    }
    const index = indexOf.get(node.id) ?? -1;
    const container = spans.find((sp) =>
      index >= sp.startIndex && index <= sp.endIndex
    );
    if (container) step.loopId = container.loopId;
    steps.push(step);
    currentId = defaultNext(def, node);
  }
  return steps;
}

/** True if any Execution in the workflow waits for owner approval. */
export function workflowRequiresApproval(def: WorkflowDefinition): boolean {
  return def.nodes.some((n) => nodeRequiresApproval(n));
}

// Validation. What a builder runs before saving, and the engine before a run.

export interface ValidationIssue {
  nodeId?: string;
  message: string;
}

/** knowledge_library ids are UUIDs. */
const KNOWLEDGE_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Structural checks on a definition: trigger-first, known node types, required
 * params present, `options` values in range, and Decision-bound inputs that
 * reference a real upstream Decision output. Returns [] when valid.
 */
export function validateDefinition(def: WorkflowDefinition): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  if (def.nodes.length === 0) {
    issues.push({ message: "Workflow has no nodes." });
    return issues;
  }
  if (def.nodes[0].type !== "trigger") {
    issues.push({
      nodeId: def.nodes[0].id,
      message: "The first node must be a trigger.",
    });
  }

  if (def.knowledge !== undefined && def.knowledge !== null) {
    const { instructions, sourceIds } = def.knowledge;
    if (instructions !== undefined && typeof instructions !== "string") {
      issues.push({ message: "Workflow instructions must be text." });
    } else if ((instructions ?? "").length > MAX_WORKFLOW_INSTRUCTIONS_CHARS) {
      issues.push({
        message:
          `Workflow instructions are limited to ${MAX_WORKFLOW_INSTRUCTIONS_CHARS} characters.`,
      });
    }
    if (sourceIds !== undefined) {
      if (
        !Array.isArray(sourceIds) ||
        sourceIds.some((id) => typeof id !== "string" || !KNOWLEDGE_ID.test(id))
      ) {
        issues.push({ message: "Workflow Knowledge must be Knowledge items." });
      } else if (sourceIds.length > MAX_WORKFLOW_KNOWLEDGE_SOURCES) {
        issues.push({
          message:
            `Pick up to ${MAX_WORKFLOW_KNOWLEDGE_SOURCES} Knowledge items for a workflow.`,
        });
      }
    }
  }

  const byId = new Map(def.nodes.map((n) => [n.id, n] as const));

  for (const n of def.nodes) {
    const spec = NODE_TYPES[n.type];
    if (!spec) {
      issues.push({ nodeId: n.id, message: `Unknown node type "${n.type}".` });
      continue;
    }
    for (const p of spec.parameters) {
      const value = n.parameters[p.key];
      const present = value !== undefined && value !== null;
      if (!present) {
        if (!p.optional) {
          issues.push({
            nodeId: n.id,
            message: `Missing required parameter "${p.key}".`,
          });
        }
        continue;
      }
      if (
        p.type === "options" && p.options &&
        !p.options.some((o) => o.value === value)
      ) {
        issues.push({
          nodeId: n.id,
          message: `"${String(value)}" is not a valid ${p.key}.`,
        });
      }
      if (p.type === "binding") {
        const b = value as unknown as Binding;
        if (b && b.source === "decision") {
          const from = byId.get(b.nodeId);
          if (!from || from.type !== "decision") {
            issues.push({
              nodeId: n.id,
              message:
                `Binding references "${b.nodeId}", which is not a Decision node.`,
            });
          } else {
            const outputs =
              (from.parameters.output as unknown as OutputField[]) ?? [];
            if (!outputs.some((o) => o.key === b.field)) {
              issues.push({
                nodeId: n.id,
                message:
                  `Binding references output "${b.field}" not produced by "${b.nodeId}".`,
              });
            }
          }
        }
      }
    }
  }

  // Loops: the repeated blocks must exist and sit directly below the loop,
  // and loops never nest. The engine counts rounds for one loop at a time.
  const spans: LoopSpan[] = [];
  for (const n of def.nodes) {
    if (n.type !== "loop") continue;
    const span = loopSpan(def, n);
    if (span) {
      spans.push(span);
      continue;
    }
    const bodyEnd = n.parameters.bodyEnd;
    issues.push({
      nodeId: n.id,
      message: typeof bodyEnd === "string" && byId.has(bodyEnd)
        ? "The blocks a loop repeats must sit directly below it."
        : "Add at least one block inside this loop.",
    });
  }
  for (const span of spans) {
    for (let i = span.startIndex; i <= span.endIndex; i++) {
      const inner = def.nodes[i];
      if (inner.type === "loop") {
        issues.push({
          nodeId: inner.id,
          message: "A loop can't sit inside another loop.",
        });
      }
      if (inner.type === "trigger") {
        issues.push({
          nodeId: inner.id,
          message: "A trigger can't sit inside a loop.",
        });
      }
    }
  }

  return issues;
}
